/**
 * When a Brief run finishes, did its paid Concepts get their ad copy?
 *
 * WHY THIS EXISTS (ITC run cfdfda60, 2026-10-01): `write_paid_copy` wrote 0 of 3
 * ad units (two writing-model failures, one claims-gate hold) and the run read
 * `succeeded`, the dialog said "Ready for your review", and the Brief page showed
 * no blocker: the workspace only surfaced a step whose `finding_kind` was
 * `partial`, and a step that wrote NOTHING is `none`.
 *
 * The run stays `succeeded`: the Concepts it wrote are the deliverable and a copy
 * failure must never discard them (`write_paid_copy` never fails the run). What
 * changes is that the outcome is stated, with the reasons, wherever the run is
 * read. `brief_runs.status` has no partial value and a CHECK on it, so this is
 * derived from the step row at read time rather than stored.
 *
 * Pure. No I/O.
 */

export interface CopyShortfallStep {
  stepKey?: string | null;
  label?: string | null;
  status: string;
  detail?: Record<string, unknown> | null;
}

export interface CopyShortfall {
  /** Paid Concepts the step was asked to write copy for; null when the step failed before counting. */
  totalConcepts: number | null;
  unwritten: number | null;
  reasons: string[];
  /** Each unwritten Concept with its title (when the run recorded one) and why, in plain words. */
  concepts: { title: string | null; reason: string }[];
  /** "Copy wasn't written for 3 of 3 Concepts: ..." */
  sentence: string;
}

const COPY_STEP_KEY = 'write_paid_copy';
const COPY_STEP_LABEL = 'Write the ad copy';

export function isCopyStep(step: Pick<CopyShortfallStep, 'stepKey' | 'label'>): boolean {
  return step.stepKey === COPY_STEP_KEY || step.label === COPY_STEP_LABEL;
}

const MODEL_UNAVAILABLE =
  /credit|quota|resource exhausted|rate[ _-]?limit|\b429\b|timeout|timed out|aborted|overloaded|unavailable|no output generated|no object generated|did not match schema/i;

type PerConcept = {
  outcome?: unknown;
  reason?: unknown;
  heldByClaimsGate?: unknown;
  conceptTitle?: unknown;
  conceptId?: unknown;
};

const GATE_LEAD =
  /^The copy kept making a claim the evidence on file does not support, so none was saved\.\s*/i;

/** The first problem the claims check named, in the check's own words, or null. */
function firstGateProblem(reason: unknown): string | null {
  if (typeof reason !== 'string') return null;
  const detail = reason.replace(GATE_LEAD, '').trim();
  if (!detail) return null;
  const first = detail.split(/(?<=\.)\s+(?=[A-Z])/)[0]!.trim();
  return first.length > 220 ? `${first.slice(0, 220).replace(/\s+\S*$/, '')}…` : first;
}

/** One Concept's reason for a person: the general cause, then the first specific problem. */
function plainReasonOf(entry: PerConcept): string {
  const general = reasonOf(entry) ?? 'no outcome was recorded';
  if (entry.outcome === 'refused' && entry.heldByClaimsGate === true) {
    const problem = firstGateProblem(entry.reason);
    return problem
      ? `the copy made a claim the evidence does not support, so none was saved. First problem: ${problem}`
      : general;
  }
  return general;
}

const QUOTA_REFUSED = /exceeded your current quota|quota exceeded|resource[ _]exhausted/i;

/** "Google refused the request: the API key's quota for gemini-3.1-pro is exhausted" or null. */
function quotaReason(fullReason: string): string | null {
  const reason =
    fullReason.split(/; (?=(?:Vertex Express|Gemini API|AI Gateway) \()/)[0] ?? fullReason;
  if (!QUOTA_REFUSED.test(reason)) return null;
  const model = /\bmodel:\s*([\w.-]+)/i.exec(reason)?.[1];
  return model
    ? `Google refused the request: the API key's quota for ${model} is exhausted`
    : "Google refused the request: the API key's quota is exhausted";
}

function reasonOf(entry: PerConcept): string | null {
  switch (entry.outcome) {
    case 'written':
      return null;
    case 'unsupported_platform':
      return 'its platform is not one Mora writes ad copy for yet';
    case 'refused':
      return entry.heldByClaimsGate === true
        ? 'the copy kept making a claim the evidence does not support, so none was saved'
        : 'the evidence on file has no claim the copy could cite';
    case 'failed': {
      const quota = typeof entry.reason === 'string' ? quotaReason(entry.reason) : null;
      if (quota) return quota;
      return typeof entry.reason === 'string' && MODEL_UNAVAILABLE.test(entry.reason)
        ? 'the writing model was unavailable'
        : 'the copy step hit an error';
    }
    default:
      return 'no outcome was recorded';
  }
}

function plural(n: number, one: string): string {
  return `${n} ${one}${n === 1 ? '' : 's'}`;
}

/**
 * The shortfall for a run's steps, or null when every paid Concept got copy (or
 * there were no paid Concepts, or the step has not finished).
 */
export function copyShortfall(
  steps: readonly CopyShortfallStep[],
  options: { conceptsWithCopy?: ReadonlySet<string> } = {},
): CopyShortfall | null {
  const step = steps.find(isCopyStep);
  if (!step) return null;
  const detail = step.detail ?? null;
  const total = typeof detail?.totalPaidConcepts === 'number' ? detail.totalPaidConcepts : null;

  if (step.status === 'failed' && total === null) {
    return {
      totalConcepts: null,
      unwritten: null,
      reasons: ['the ad-copy step failed unexpectedly'],
      concepts: [],
      sentence:
        "Copy wasn't written for these Concepts: the ad-copy step failed unexpectedly. The Concepts themselves are saved.",
    };
  }
  if (step.status !== 'succeeded' && step.status !== 'failed') return null;
  if (total === null || total === 0) return null;

  // The step row records the run. A Concept the run missed that has copy now is no longer short of it.
  const recorded = Array.isArray(detail?.perConcept) ? (detail.perConcept as PerConcept[]) : [];
  const perConcept = recorded.filter(
    (entry) =>
      entry.outcome === 'written' ||
      typeof entry.conceptId !== 'string' ||
      !options.conceptsWithCopy?.has(entry.conceptId),
  );
  const writtenAtRun = typeof detail?.written === 'number' ? detail.written : 0;
  const written = writtenAtRun + recorded.length - perConcept.length;
  const unwritten = Math.max(0, total - written);
  if (unwritten === 0) return null;

  const counts = new Map<string, number>();
  for (const entry of perConcept) {
    const reason = reasonOf(entry);
    if (reason) counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }
  const reasons = [...counts.entries()].map(([reason, count]) => `${count} because ${reason}`);

  const head = `Copy wasn't written for ${unwritten} of ${plural(total, 'Concept')}`;
  const concepts = perConcept
    .filter((entry) => reasonOf(entry) !== null)
    .map((entry) => ({
      title:
        typeof entry.conceptTitle === 'string' && entry.conceptTitle.trim()
          ? entry.conceptTitle.trim()
          : null,
      reason: plainReasonOf(entry),
    }));
  const named = concepts.length > 0 && concepts.every((concept) => concept.title !== null);
  const body = named
    ? concepts
        .map((concept) => `“${concept.title}” — ${concept.reason.replace(/\.$/, '')}`)
        .join('; ')
    : reasons.join('; ');
  return {
    totalConcepts: total,
    unwritten,
    reasons,
    concepts,
    sentence: body.length > 0 ? `${head}: ${body}.` : `${head}.`,
  };
}

/** The run's status word for a person: `succeeded` with a copy shortfall is not "Ready". */
export function briefRunOutcomeLabel(
  status: string,
  steps: readonly CopyShortfallStep[],
  fallback: string,
  options: { conceptsWithCopy?: ReadonlySet<string> } = {},
): string {
  if (status !== 'succeeded') return fallback;
  const shortfall = copyShortfall(steps, options);
  if (!shortfall) return fallback;
  return shortfall.unwritten !== null && shortfall.totalConcepts !== null
    ? `Concepts ready, copy not written for ${shortfall.unwritten} of ${shortfall.totalConcepts}`
    : 'Concepts ready, copy not written';
}
